import { storeKey } from "@memorang/helpers/storage";
import { createContext, useCallback, useContext, useState } from "react";
import { useColorScheme } from "react-native";
import {
	type ThemePreference,
	getOppositeThemePreference,
	resolveIsDarkMode,
} from "./theme-preference";

type ThemeContextProps = {
	themePreference: ThemePreference;
	setThemePreference: (preference: ThemePreference) => void;
	isDarkMode: boolean;
	toggleDarkMode: () => void;
};

const ThemeContext = createContext<ThemeContextProps>({
	themePreference: "system",
	setThemePreference: () => {
		throw new Error("setThemePreference is not implemented");
	},
	isDarkMode: false,
	toggleDarkMode: () => {
		throw new Error("toggleDarkMode is not implemented");
	},
});

const ThemeContextProvider = ({
	children,
	initThemePreference,
}: {
	children: React.ReactNode;
	initThemePreference: ThemePreference;
}) => {
	const systemColorScheme = useColorScheme();
	const [themePreference, setThemePreferenceState] =
		useState<ThemePreference>(initThemePreference);
	const isDarkMode = resolveIsDarkMode(themePreference, systemColorScheme);

	const setThemePreference = useCallback((preference: ThemePreference) => {
		setThemePreferenceState(preference);
		storeKey("themePreference", preference);
	}, []);

	const toggleDarkMode = useCallback(() => {
		setThemePreference(getOppositeThemePreference(isDarkMode));
	}, [isDarkMode, setThemePreference]);

	return (
		<ThemeContext.Provider
			value={{
				themePreference,
				setThemePreference,
				isDarkMode,
				toggleDarkMode,
			}}
		>
			{children}
		</ThemeContext.Provider>
	);
};

const useThemeContext = () => {
	const context = useContext(ThemeContext);

	if (!context) {
		throw new Error(
			"useThemeContext must be used within a ThemeContextProvider",
		);
	}

	return context;
};
export { ThemeContextProvider, useThemeContext };
